import React, { useState, useEffect } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import Logo from "@/components/Logo";
import SearchAutocomplete from "@/components/SearchAutocomplete";
import NotificationBell from "@/components/NotificationBell";
import CurrencyPicker from "@/components/CurrencyPicker";
import { Search, Plus, MessageCircle, LogOut, LayoutDashboard, User as UserIcon, Menu, X, ShieldCheck, BadgeCheck, Crown, Map as MapIcon, LifeBuoy, MapPin, Navigation, Banknote, Hammer, Clapperboard, Store, ChevronDown, LayoutGrid, Package, Sparkles } from "lucide-react";
import { PRODUCT_CATEGORIES, categoryHref, categorySlug } from "@/data/categories";

const Navbar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [fulfilCount, setFulfilCount] = useState(0);

  useEffect(() => {
    if (user?.role !== "admin") { setFulfilCount(0); return; }
    let alive = true;
    const fetchFulfil = async () => {
      try {
        const r = await api.get("/admin/supplier-orders/needs-action-count");
        if (alive) setFulfilCount(r.data?.count || 0);
      } catch (_) { /* ignore */ }
    };
    fetchFulfil();
    const t = setInterval(fetchFulfil, 30000);
    return () => { alive = false; clearInterval(t); };
  }, [user]);

  useEffect(() => {
    if (!user) { setUnreadMessages(0); return; }
    let alive = true;
    const fetchUnread = async () => {
      try {
        const r = await api.get("/conversations");
        if (!alive) return;
        const total = (r.data || []).reduce((s, c) => s + (c.unread_count || 0), 0);
        setUnreadMessages(total);
      } catch (_) { /* ignore */ }
    };
    fetchUnread();
    const t = setInterval(fetchUnread, 15000);
    return () => { alive = false; clearInterval(t); };
  }, [user]);

  return (
    <header className="sticky top-0 z-50 bg-white/85 backdrop-blur-xl border-b border-black/5">
      <div className="container-narrow flex items-center gap-6 h-16">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2.5 shrink-0 group">
          <Logo size={40} withWordmark wordmarkClassName="text-[0.5rem]"/>
        </Link>

        <SearchAutocomplete
          className="hidden md:flex flex-1 max-w-xl"
          inputClassName="bg-[#F3EFE9] border-transparent focus-visible:bg-white"
        />

        <nav className="hidden md:flex items-center gap-1">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="nav-products" className="px-3 py-2 text-sm font-medium rounded-md hover:bg-black/5 flex items-center gap-1 focus:outline-none">
                Products <ChevronDown className="w-3.5 h-3.5" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-[460px] p-2" data-testid="nav-products-menu">
              <DropdownMenuItem onClick={() => navigate("/browse?type=product")} data-testid="nav-cat-all" className="font-semibold text-[#C05A3C]">
                <LayoutGrid className="w-4 h-4 mr-2" /> All products
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <div className="grid grid-cols-2 gap-0.5">
                {PRODUCT_CATEGORIES.map(({ label, icon: Icon }) => (
                  <DropdownMenuItem
                    key={label}
                    onClick={() => navigate(categoryHref(label))}
                    data-testid={`nav-cat-${categorySlug(label)}`}
                    className="cursor-pointer"
                  >
                    <Icon className="w-4 h-4 mr-2 text-[#C05A3C]" /> {label}
                  </DropdownMenuItem>
                ))}
              </div>
            </DropdownMenuContent>
          </DropdownMenu>
          <NavLink to="/browse?type=service" data-testid="nav-services" className={({isActive}) => `px-3 py-2 text-sm font-medium rounded-md hover:bg-black/5 ${isActive ? "text-[#C05A3C]" : ""}`}>Services</NavLink>
          <NavLink to="/locations" data-testid="nav-locations" className={({isActive}) => `px-3 py-2 text-sm font-medium rounded-md hover:bg-black/5 flex items-center gap-1 ${isActive ? "text-[#C05A3C]" : ""}`}>
            <MapPin className="w-3.5 h-3.5"/> Locations
          </NavLink>
          <NavLink to="/near-me" data-testid="nav-near-me" className={({isActive}) => `px-3 py-2 text-sm font-medium rounded-md hover:bg-black/5 flex items-center gap-1 ${isActive ? "text-[#C05A3C]" : ""}`}>
            <Navigation className="w-3.5 h-3.5"/> Near me
          </NavLink>
          <NavLink to="/map" data-testid="nav-map" className={({isActive}) => `px-3 py-2 text-sm font-medium rounded-md hover:bg-black/5 flex items-center gap-1 ${isActive ? "text-[#C05A3C]" : ""}`}>
            <MapIcon className="w-3.5 h-3.5"/> Map
          </NavLink>
          <NavLink to="/makers" data-testid="nav-makers" className={({isActive}) => `px-3 py-2 text-sm font-medium rounded-md hover:bg-black/5 flex items-center gap-1 ${isActive ? "text-[#C05A3C]" : ""}`}>
            <Hammer className="w-3.5 h-3.5"/> Makers
          </NavLink>
          <NavLink to="/movies" data-testid="nav-movies" className={({isActive}) => `px-3 py-2 text-sm font-medium rounded-md hover:bg-black/5 flex items-center gap-1 ${isActive ? "text-[#C05A3C]" : ""}`}>
            <Clapperboard className="w-3.5 h-3.5"/> Movies
          </NavLink>
          <NavLink to="/shops" data-testid="nav-shops" className={({isActive}) => `px-3 py-2 text-sm font-medium rounded-md hover:bg-black/5 flex items-center gap-1 ${isActive ? "text-[#C05A3C]" : ""}`}>
            <Store className="w-3.5 h-3.5"/> Shops
          </NavLink>
          <NavLink to="/shopper" data-testid="nav-ai-shopper" className={({isActive}) => `px-3 py-2 text-sm font-medium rounded-md hover:bg-black/5 flex items-center gap-1 ${isActive ? "text-[#C05A3C]" : "text-[#C05A3C]"}`}>
            <Sparkles className="w-3.5 h-3.5" /> AI Shopper
          </NavLink>
          <NavLink to="/pro" data-testid="nav-pro" className={({isActive}) => `px-3 py-2 text-sm font-medium rounded-md hover:bg-black/5 flex items-center gap-1 ${isActive ? "text-[#D9923B]" : "text-[#D9923B]"}`}>
            <Crown className="w-3.5 h-3.5"/> Pro
          </NavLink>
        </nav>

        <div className="ml-auto flex items-center gap-2">
          <CurrencyPicker className="hidden sm:inline-flex" />
          {user ? (
            <>
              <Button data-testid="nav-sell-btn" onClick={() => navigate(user.verification_status === "approved" ? "/create" : "/verify")} className="bg-[#C05A3C] hover:bg-[#D46B4E] text-white rounded-md hidden sm:inline-flex">
                <Plus className="w-4 h-4 mr-1" /> Sell
              </Button>
              {user.role === "admin" && (
                <Button data-testid="nav-fulfil-btn" variant="ghost" size="icon" onClick={() => navigate("/fulfil")} className="rounded-full relative" title="Orders to fulfil">
                  <Package className="w-5 h-5" />
                  {fulfilCount > 0 && (
                    <span data-testid="nav-fulfil-count" className="absolute top-0.5 right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-[#D9923B] text-[#1A2024] text-[10px] font-semibold flex items-center justify-center">
                      {fulfilCount > 9 ? "9+" : fulfilCount}
                    </span>
                  )}
                </Button>
              )}
              <Button data-testid="nav-messages-btn" variant="ghost" size="icon" onClick={() => navigate("/dashboard/messages")} className="rounded-full relative">
                <MessageCircle className="w-5 h-5" />
                {unreadMessages > 0 && (
                  <span data-testid="nav-messages-unread" className="absolute top-0.5 right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-[#C05A3C] text-white text-[10px] font-semibold flex items-center justify-center">
                    {unreadMessages > 9 ? "9+" : unreadMessages}
                  </span>
                )}
              </Button>
              <NotificationBell />
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button data-testid="nav-user-menu" className="rounded-full focus:outline-none focus:ring-2 focus:ring-[#C05A3C]/50">
                    <Avatar className="w-9 h-9 border border-black/10">
                      <AvatarImage src={user.picture || undefined} />
                      <AvatarFallback className="bg-[#233348] text-white text-sm">
                        {user.name?.slice(0, 1).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel className="font-sans-ui">
                    <div className="text-sm font-semibold truncate flex items-center gap-1">
                      {user.name}
                      {user.verified_badge && <BadgeCheck className="w-4 h-4 text-[#C05A3C]"/>}
                    </div>
                    <div className="text-xs text-muted-foreground truncate">{user.email}</div>
                    <div className="flex gap-1 mt-1">
                      {user.is_pro && <Badge className="bg-[#D9923B] text-[#1A2024] text-[10px]"><Crown className="w-2.5 h-2.5 mr-0.5"/> PRO</Badge>}
                      {user.role === "admin" && <Badge className="bg-[#1A2024] text-white text-[10px]">ADMIN</Badge>}
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate("/dashboard")} data-testid="menu-dashboard"><LayoutDashboard className="w-4 h-4 mr-2"/> Dashboard</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/dashboard/shop")} data-testid="menu-my-shop"><Store className="w-4 h-4 mr-2"/> My Shop</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/dashboard/payouts")} data-testid="menu-payouts"><Banknote className="w-4 h-4 mr-2"/> Payouts · Stripe</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/dashboard/messages")} data-testid="menu-messages"><MessageCircle className="w-4 h-4 mr-2"/> Messages</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/verify")} data-testid="menu-verify"><ShieldCheck className="w-4 h-4 mr-2"/> Verification</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/pro")} data-testid="menu-pro" className={user.is_pro ? "" : "text-[#D9923B]"}><Crown className="w-4 h-4 mr-2"/> {user.is_pro ? "Manage Pro" : "Upgrade to Pro"}</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/create")} data-testid="menu-create"><Plus className="w-4 h-4 mr-2"/> New listing</DropdownMenuItem>
                  {user.role === "admin" && <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="menu-admin"><UserIcon className="w-4 h-4 mr-2"/> Admin panel</DropdownMenuItem>}
                  {user.role === "admin" && <DropdownMenuItem onClick={() => navigate("/fulfil")} data-testid="menu-fulfil"><Package className="w-4 h-4 mr-2"/> Orders to Fulfil</DropdownMenuItem>}
                  {user.role === "admin" && <DropdownMenuItem onClick={() => navigate("/admin/complaints")} data-testid="menu-admin-complaints"><LifeBuoy className="w-4 h-4 mr-2"/> Admin · Cases</DropdownMenuItem>}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={async () => { await logout(); navigate("/"); }} data-testid="menu-logout"><LogOut className="w-4 h-4 mr-2"/> Log out</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          ) : (
            <>
              <Button data-testid="nav-login-btn" variant="ghost" onClick={() => navigate("/login")} className="hidden sm:inline-flex">Log in</Button>
              <Button data-testid="nav-signup-btn" onClick={() => navigate("/register")} className="bg-[#C05A3C] hover:bg-[#D46B4E] text-white rounded-md">Join AfriMarket</Button>
            </>
          )}
          <button className="md:hidden p-2" onClick={() => setMobileOpen(!mobileOpen)} data-testid="nav-mobile-toggle">
            {mobileOpen ? <X className="w-5 h-5"/> : <Menu className="w-5 h-5"/>}
          </button>
        </div>
      </div>
      {mobileOpen && (
        <div className="md:hidden border-t border-black/5 bg-white px-4 py-3 space-y-2">
          <SearchAutocomplete
            className="block"
            placeholder="Search…"
            inputClassName="bg-[#F3EFE9] border-transparent"
            onNavigate={() => setMobileOpen(false)}
          />
          <button onClick={() => { setMobileOpen(false); navigate("/browse?type=product"); }} className="block w-full text-left py-2 text-sm">Products</button>
          <div className="pl-3 border-l border-black/5 grid grid-cols-2 gap-x-2">
            {PRODUCT_CATEGORIES.map(({ label, icon: Icon }) => (
              <button
                key={label}
                onClick={() => { setMobileOpen(false); navigate(categoryHref(label)); }}
                data-testid={`nav-mobile-cat-${categorySlug(label)}`}
                className="flex items-center gap-1.5 py-1.5 text-xs text-muted-foreground hover:text-[#C05A3C] text-left"
              >
                <Icon className="w-3.5 h-3.5 shrink-0" /> <span className="truncate">{label}</span>
              </button>
            ))}
          </div>
          <button onClick={() => { setMobileOpen(false); navigate("/browse?type=service"); }} className="block w-full text-left py-2 text-sm">Services</button>
          <button onClick={() => { setMobileOpen(false); navigate("/locations"); }} className="block w-full text-left py-2 text-sm">Shop by location</button>
          <button onClick={() => { setMobileOpen(false); navigate("/near-me"); }} className="block w-full text-left py-2 text-sm">Near me</button>
          <button onClick={() => { setMobileOpen(false); navigate("/map"); }} className="block w-full text-left py-2 text-sm">Map view</button>
          <button onClick={() => { setMobileOpen(false); navigate("/makers"); }} className="block w-full text-left py-2 text-sm">Makers</button>
          <button onClick={() => { setMobileOpen(false); navigate("/movies"); }} className="block w-full text-left py-2 text-sm">Movies</button>
          <button onClick={() => { setMobileOpen(false); navigate("/shops"); }} className="block w-full text-left py-2 text-sm">Shops</button>
          {user && (
            <button onClick={() => { setMobileOpen(false); navigate("/create"); }} className="block w-full text-left py-2 text-sm text-[#C05A3C] font-semibold">+ Sell something</button>
          )}
          <div className="pt-2 border-t border-black/5">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Display currency</div>
            <CurrencyPicker className="w-full justify-start" />
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
